/**
 * Prompt suggestions on Eval Results (PROC-265, PROC-266), laid out as
 * PROC-258's mock. Every kind gets the same card, labelled with the prompt
 * it edits; a shared prompt with suggestions from more than one
 * org is reviewed from SharedPromptSuggestions instead.
 *
 * Each week Rio drafts a prompt change for the org's strongest edit themes.
 * One card at a time shows the theme with example edits, the org's edit rate
 * when it was drafted, and the draft diffed against the live prompt. Only
 * suggestions still to review (new, then testing) are listed; shipped ones
 * are under AcceptedSuggestions. Dismiss retires the theme for good; Edit
 * changes the suggestion's draft only, never the prompt or its
 * `draft_overrides`. Review & test opens the prompt's Evaluations page on the
 * suggestion. A stale one offers only Re-draft and Dismiss.
 */
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, type ReactNode, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import remarkBreaks from "remark-breaks";

import { Button, Dialog, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  type PromptSuggestion,
  type PromptSuggestionExample,
  PromptSuggestionKindEnum,
  PromptSuggestionStatusEnum,
  getGetPromptSuggestionQueryKey,
  getListPromptSuggestionsQueryKey,
  getListSharedPromptChangesQueryKey,
  useDismissPromptSuggestion,
  useListPromptSuggestions,
  useListSharedPromptChanges,
  useRedraftPromptSuggestion,
  useUpdatePromptSuggestionDraft,
} from "@api/rest/types-and-hooks";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { PromptLink } from "@pages/Evaluations/PromptLink";
import { CustomerSummaryButton, SuggestionKindNote } from "@pages/Evaluations/SuggestionApproval";
import { METRICS_WINDOW_LABEL, formatPercent } from "@pages/Evaluations/evalResultsProduction";
import {
  type DraftFormValues,
  EXAMPLES_SHOWN,
  EXAMPLE_KIND_LABELS,
  ExampleKind,
  type KeyedCatalogs,
  REDRAFT_POLL_MS,
  SUGGESTION_FIELD_LABELS,
  SUGGESTION_STATUS_LABELS,
  type SuggestionDraft,
  SuggestionField,
  type SuggestionFieldValue,
  approvalScopeLabel,
  draftEdits,
  draftFormValues,
  exampleCountsLabel,
  exampleKind,
  multiOrgSharedGroups,
  needsRedraft,
  proposedFields,
  redraftNote,
  reviewPositionLabel,
  suggestionBase,
  suggestionChanges,
  suggestionDraft,
  suggestionEvaluationsPath,
} from "@pages/Evaluations/promptSuggestionFields";
import { useSuggestionCatalogs } from "@pages/Evaluations/useSuggestionCatalogs";
import { PromptInputsSelect } from "@pages/Prompts/PromptInputsSelect";
import { PromptRoleSelect } from "@pages/Prompts/PromptRoleSelect";
import { PromptTechnicalInstructionsSelect } from "@pages/Prompts/PromptTechnicalInstructionsSelect";
import { PromptTemplatedInstructionsSelect } from "@pages/Prompts/PromptTemplatedInstructionsSelect";
import { DiffLineKind, diffLineRows, foldUnchanged } from "@pages/Prompts/promptLineDiff";

const responseDetail = (resp: { data: unknown; status: number }): string =>
  (resp.data as { detail?: string } | undefined)?.detail ?? `HTTP ${resp.status}`;

const FieldHeading: FC<{ field: SuggestionFieldValue }> = ({ field }) => (
  <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
    {SUGGESTION_FIELD_LABELS[field]}
  </span>
);

interface EditDraftDialogProps {
  suggestion: PromptSuggestion;
  draft: SuggestionDraft;
  onClose: () => void;
}

/** Edits the fields the draft proposes. Mounted only while open, so it
 *  starts from the latest saved draft. */
const EditDraftDialog: FC<EditDraftDialogProps> = ({ suggestion, draft, onClose }) => {
  const queryClient = useQueryClient();
  const fields = useMemo(() => proposedFields(draft), [draft]);
  const initial = useMemo(() => draftFormValues(draft), [draft]);
  const updateMutation = useUpdatePromptSuggestionDraft();

  const { register, setValue, watch, onSubmit, RegisteredFormTextArea } = useFormUtils<DraftFormValues>({
    defaultValues: initial,
    mutationFn: async (values) => {
      const result = draftEdits(fields, values, initial);
      if ("error" in result) {
        notifyError(result.error);
        return;
      }
      if (Object.keys(result.draft).length === 0) {
        onClose();
        return;
      }
      try {
        const resp = await updateMutation.mutateAsync({ suggestionId: suggestion.id, data: { draft: result.draft } });
        if (resp.status !== 200) {
          notifyError(responseDetail(resp));
          return;
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getListPromptSuggestionsQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getListSharedPromptChangesQueryKey() }),
        ]);
        notifySuccess("Draft saved.");
        onClose();
      } catch (err) {
        notifyError(err instanceof Error ? err.message : "Failed to save the draft.");
      }
    },
  });

  // Fields managed via setValue (not native register).
  useEffect(() => {
    register("role");
    register("templated_instructions");
    register("technical_instructions");
    register("input_keys");
  }, [register]);

  const fieldEditor = (field: SuggestionFieldValue) => {
    switch (field) {
      case SuggestionField.ROLE:
        return (
          <PromptRoleSelect
            value={watch("role") ?? ""}
            onChange={(next) => setValue("role", next, { shouldDirty: true })}
          />
        );
      case SuggestionField.TEMPLATED_INSTRUCTIONS:
        return (
          <PromptTemplatedInstructionsSelect
            value={watch("templated_instructions") ?? ""}
            onChange={(next) => setValue("templated_instructions", next, { shouldDirty: true })}
          />
        );
      case SuggestionField.TECHNICAL_INSTRUCTIONS:
        return (
          <PromptTechnicalInstructionsSelect
            value={watch("technical_instructions") ?? ""}
            onChange={(next) => setValue("technical_instructions", next, { shouldDirty: true })}
          />
        );
      case SuggestionField.INPUTS:
        return (
          <PromptInputsSelect
            value={watch("input_keys") ?? []}
            onChange={(next) => setValue("input_keys", next, { shouldDirty: true })}
          />
        );
      case SuggestionField.INSTRUCTIONS:
        return <RegisteredFormTextArea formKey="instructions" rows={16} />;
      case SuggestionField.SAMPLE_OUTPUT:
        return <RegisteredFormTextArea formKey="sample_output" rows={6} />;
      case SuggestionField.RESPONSE_SCHEMA:
        return (
          <RegisteredFormTextArea
            formKey="response_schema"
            rows={8}
            placeholder='{"type": "object", "properties": {...}}'
          />
        );
    }
  };

  return (
    <Dialog
      open
      setOpen={(open) => {
        if (!open) onClose();
      }}
      title={`Edit suggestion: ${suggestion.theme_title}`}
      size="4xl"
      overflowBehavior="yAuto"
      primaryButton={{
        text: updateMutation.isPending ? "Saving…" : "Save changes",
        onClick: () => void onSubmit(),
        disabled: updateMutation.isPending,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose, disabled: updateMutation.isPending }}
    >
      <div className="flex flex-col gap-4">
        <Paragraph size="small" colorType="secondary">
          Saves to this suggestion only. The prompt, and its draft on the Evaluations page, stay as they are. Clear a
          field to drop that change.
        </Paragraph>
        {fields.map((field) => (
          <div key={field} className="flex flex-col gap-1">
            <FieldHeading field={field} />
            {fieldEditor(field)}
          </div>
        ))}
      </div>
    </Dialog>
  );
};

const SectionLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="text-xs font-semibold uppercase tracking-wide text-text-secondary">{children}</div>
);

// The card lists only suggestions still to review.
const STATUS_BADGE_CLASSES: Partial<Record<PromptSuggestionStatusEnum, string>> = {
  [PromptSuggestionStatusEnum.new]: "border-border-subtle bg-bg-default text-text-secondary",
  [PromptSuggestionStatusEnum.testing]: "border-amber-200 bg-amber-50 text-amber-700",
};

const StatusBadge: FC<{ status: PromptSuggestionStatusEnum }> = ({ status }) => (
  <span
    className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STATUS_BADGE_CLASSES[status] ?? ""}`}
  >
    {SUGGESTION_STATUS_LABELS[status]}
  </span>
);

/** One clinician edit, styled like the proposed change: AI text struck
 *  through, the clinician's text highlighted. */
const ExampleEdit: FC<{ example: PromptSuggestionExample }> = ({ example }) => {
  const kind = exampleKind(example);
  return (
    <div className="flex flex-col gap-y-1 rounded-md border border-border-subtle bg-bg-default p-2 text-sm">
      <span className="text-xs text-text-tertiary">
        {example.category} · {EXAMPLE_KIND_LABELS[kind]}
      </span>
      {kind !== ExampleKind.ADDED ? (
        <div className="whitespace-pre-wrap break-words rounded bg-red-50 px-2 py-1 text-text-error-primary line-through">
          {example.ai_text}
        </div>
      ) : null}
      {kind !== ExampleKind.DELETED ? (
        <div className="whitespace-pre-wrap break-words rounded bg-emerald-50 px-2 py-1 text-text-success-primary">
          {example.user_text}
        </div>
      ) : null}
      {example.reason ? <p className="text-xs text-text-secondary">Why: {example.reason}</p> : null}
    </div>
  );
};

const ExampleEdits: FC<{ suggestion: PromptSuggestion }> = ({ suggestion }) => {
  const [showAll, setShowAll] = useState(false);
  const examples = suggestion.theme_examples;
  if (examples.length === 0) return null;
  const shown = showAll ? examples : examples.slice(0, EXAMPLES_SHOWN);
  const counts = exampleCountsLabel(suggestion);
  return (
    <div className="mt-1 flex flex-col gap-y-2">
      <div>
        <SectionLabel>Example edits</SectionLabel>
        {counts ? (
          // Counts cover every edit in the theme's categories, not only the
          // theme's, so they're labelled by category.
          <p className="mt-0.5 text-xs text-text-tertiary">
            {suggestion.theme_categories.join(", ")} edits on this prompt, {METRICS_WINDOW_LABEL}: {counts}
          </p>
        ) : null}
      </div>
      {shown.map((example, i) => (
        // Examples have no id; the list is fixed per suggestion.
        <ExampleEdit key={i} example={example} />
      ))}
      {examples.length > EXAMPLES_SHOWN ? (
        <button
          type="button"
          onClick={() => setShowAll((all) => !all)}
          className="self-start text-xs text-text-secondary hover:text-text-primary"
        >
          {showAll ? "Show fewer" : `Show all ${examples.length}`}
        </button>
      ) : null}
    </div>
  );
};

/** One field's change, as in the mock: unchanged lines plain, removals struck
 *  through, additions highlighted, long unchanged runs folded. */
const ProposedDiff: FC<{ before: string; after: string; mono: boolean }> = ({ before, after, mono }) => {
  const rows = useMemo(() => foldUnchanged(diffLineRows(before, after)), [before, after]);
  return (
    <div className={`flex max-h-96 flex-col gap-1 overflow-y-auto ${mono ? "font-mono text-xs" : "text-sm"}`}>
      {rows.map((row) => {
        if (row.kind === DiffLineKind.SKIPPED) {
          return (
            <div key={row.key} className="px-2 text-xs text-text-tertiary">
              ⋯ {row.count} unchanged lines
            </div>
          );
        }
        // Mosaic has no error/success background tokens; red-50 and
        // emerald-50 are the closest, as on the Review draft page.
        if (row.kind === DiffLineKind.REMOVED) {
          return (
            <div
              key={row.key}
              className="whitespace-pre-wrap break-words rounded bg-red-50 px-2 py-1 text-text-error-primary line-through"
            >
              −{row.line}
            </div>
          );
        }
        if (row.kind === DiffLineKind.ADDED) {
          return (
            <div
              key={row.key}
              className="whitespace-pre-wrap break-words rounded bg-emerald-50 px-2 py-1 text-text-success-primary"
            >
              + {row.line}
            </div>
          );
        }
        return (
          <div key={row.key} className="whitespace-pre-wrap break-words px-2 text-text-secondary">
            {row.line || " "}
          </div>
        );
      })}
    </div>
  );
};

const NavButton: FC<{ label: string; onClick: () => void; disabled: boolean; children: ReactNode }> = ({
  label,
  onClick,
  disabled,
  children,
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className="flex size-7 items-center justify-center rounded-md border border-border-subtle bg-bg-surface text-text-secondary hover:text-text-primary disabled:cursor-default disabled:opacity-40"
  >
    {children}
  </button>
);

interface SuggestionPosition {
  /** 0-based. */
  index: number;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
}

interface SuggestionDetailsProps {
  suggestion: PromptSuggestion;
  draft: SuggestionDraft;
  catalogs: KeyedCatalogs;
  catalogsPending: boolean;
  /** Null until the org list loads. */
  orgName: string | null;
}

/** A suggestion's theme, example edits, edit rate and proposed change: the
 *  card's body, also shown in Review draft's incorporate pop-up. */
export const SuggestionDetails: FC<SuggestionDetailsProps> = ({
  suggestion,
  draft,
  catalogs,
  catalogsPending,
  orgName,
}) => {
  const [notesOpen, setNotesOpen] = useState(false);
  const base = useMemo(() => suggestionBase(suggestion), [suggestion]);
  // A keyed field's raw key would diff against resolved text until its
  // catalog loads, flashing a false change.
  const changes = useMemo(
    () => (catalogsPending ? [] : suggestionChanges(base, draft, catalogs)),
    [base, draft, catalogs, catalogsPending],
  );
  const isCopy = suggestion.kind === PromptSuggestionKindEnum.org_copy;
  const isShared = suggestion.kind === PromptSuggestionKindEnum.shared_change;
  const org = orgName ?? "this organization";
  const baselineEdited = suggestion.baseline_pct_fields_edited;
  const baselineEditSize = suggestion.baseline_avg_edit_percentage;

  return (
    <>
      <div>
        <Headline as="h3" size="small">
          {suggestion.theme_title}
        </Headline>
        <p className="mt-1 text-sm text-text-secondary">
          In{" "}
          <strong className="font-semibold text-text-primary">
            {suggestion.theme_support} of {suggestion.theme_group_size}
          </strong>{" "}
          recently edited {orgName ? `${orgName} ` : ""}reports, clinicians made this edit to{" "}
          {/* The key, as the Production table names the prompt; the name on hover. */}
          <span title={suggestion.prompt_name || undefined} className="rounded bg-rio-50 px-1.5 py-0.5 font-medium">
            <PromptLink promptKey={suggestion.prompt_key} />
          </span>
          . Rio drafted a prompt change so the AI gets it right from the start.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-y-3 rounded-lg border border-border-subtle bg-bg-surface p-4">
          <SectionLabel>What clinicians keep changing</SectionLabel>
          <p className="whitespace-pre-wrap text-sm text-text-primary">{suggestion.theme_description}</p>
          {suggestion.theme_categories.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {suggestion.theme_categories.map((category) => (
                <span
                  key={category}
                  className="rounded border border-border-subtle bg-bg-default px-1.5 py-0.5 text-xs text-text-secondary"
                >
                  {category}
                </span>
              ))}
            </div>
          ) : null}
          <ExampleEdits suggestion={suggestion} />
          <div className="mt-1">
            <SectionLabel>Edit rate when drafted, {METRICS_WINDOW_LABEL}</SectionLabel>
            <p className="mt-1 text-sm text-text-primary">
              {baselineEdited !== null && baselineEditSize !== null ? (
                <>
                  {formatPercent(baselineEdited)} of fields edited · {formatPercent(baselineEditSize)} edit size
                  {suggestion.baseline_sample_size !== null
                    ? ` · ${suggestion.baseline_sample_size.toLocaleString()} fields`
                    : ""}
                </>
              ) : (
                <span className="text-text-tertiary">No scored fields for this prompt in the window.</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-y-3 rounded-lg border border-border-subtle bg-bg-surface p-4">
          <SectionLabel>
            {isCopy
              ? `Proposed change, in a copy for ${org}`
              : isShared
                ? "Proposed change to the shared prompt"
                : `Proposed change to the ${orgName ? `${orgName} ` : ""}prompt`}
          </SectionLabel>
          {catalogsPending ? (
            <span className="text-sm text-text-tertiary">Loading proposed change…</span>
          ) : changes.length === 0 ? (
            <span className="text-sm text-text-tertiary">The draft matches the current prompt.</span>
          ) : (
            changes.map((change) => (
              <div key={change.label} className="flex flex-col gap-y-1.5">
                {changes.length > 1 ? (
                  <span className="text-xs font-medium text-text-tertiary">{change.label}</span>
                ) : null}
                <ProposedDiff before={change.before} after={change.after} mono={change.format === "json"} />
              </div>
            ))
          )}
          {base === null && changes.length > 0 ? (
            <p className="text-xs text-text-tertiary">
              The current prompt wasn&apos;t captured when this was drafted, so the whole draft shows as added.
            </p>
          ) : null}
          {draft.notes ? (
            <div className="border-t border-border-subtle pt-2">
              <button
                type="button"
                onClick={() => setNotesOpen((open) => !open)}
                aria-expanded={notesOpen}
                className="flex items-center gap-x-1 text-xs text-text-secondary hover:text-text-primary"
              >
                {notesOpen ? <ChevronDownIcon className="size-3.5" /> : <ChevronRightIcon className="size-3.5" />}
                Why Rio changed it
              </button>
              {notesOpen ? (
                // The constructor writes its notes in markdown (bold, numbered lists).
                <div className="prose prose-sm mt-1.5 max-w-none text-xs text-text-primary break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>{draft.notes}</ReactMarkdown>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

interface SuggestionCardProps {
  suggestion: PromptSuggestion;
  draft: SuggestionDraft;
  catalogs: KeyedCatalogs;
  catalogsPending: boolean;
  /** Null until the org list loads. */
  orgName: string | null;
  position: SuggestionPosition;
}

const SuggestionCard: FC<SuggestionCardProps> = ({
  suggestion,
  draft,
  catalogs,
  catalogsPending,
  orgName,
  position,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmingDismiss, setConfirmingDismiss] = useState(false);
  const [editing, setEditing] = useState(false);
  const dismissMutation = useDismissPromptSuggestion();
  const redraftMutation = useRedraftPromptSuggestion();

  const dismiss = async () => {
    try {
      const resp = await dismissMutation.mutateAsync({ suggestionId: suggestion.id });
      if (resp.status !== 200) {
        notifyError(responseDetail(resp));
        return;
      }
      setConfirmingDismiss(false);
      notifySuccess("Suggestion dismissed.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getListPromptSuggestionsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getListSharedPromptChangesQueryKey() }),
      ]);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Failed to dismiss the suggestion.");
    }
  };

  const redraft = async () => {
    try {
      const resp = await redraftMutation.mutateAsync({ suggestionId: suggestion.id });
      if (resp.status !== 202) notifyError(responseDetail(resp));
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Failed to re-draft the suggestion.");
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListPromptSuggestionsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListSharedPromptChangesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getGetPromptSuggestionQueryKey(suggestion.id) }),
    ]);
  };

  // Stale or re-drafting: only Re-draft and Dismiss.
  const stale = needsRedraft(suggestion);
  const note = redraftNote(suggestion, draft);
  const beingIncorporated = suggestion.being_incorporated_into != null;
  const nothingLeft = proposedFields(draft).length === 0;
  const org = orgName ?? "this organization";

  return (
    // The mock's gradient border: Mosaic's AI gradient as a 1px frame around a
    // tinted fill. The app's own `ai` tokens point at rio by Chelsey's call
    // (index.css); this card follows the PROC-258 mock's blurple instead.
    <section className="rounded-xl bg-ai-gradient-hover p-px">
      <div className="rounded-[11px] bg-bg-surface">
        <div className="flex flex-col gap-y-4 rounded-[11px] bg-ai-gradient-transparent px-6 py-5">
          <div className="flex items-center justify-between gap-x-4">
            <div className="flex items-center gap-x-2">
              <span className="flex size-6 items-center justify-center rounded-md bg-blurple-600">
                <SparklesIcon className="size-4 text-text-oncolor" aria-hidden="true" />
              </span>
              <span className="text-sm font-semibold text-blurple-600">Rio suggests a prompt change</span>
              <span className="rounded-full border border-blurple-200 bg-blurple-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blurple-600">
                AI-generated
              </span>
              <StatusBadge status={suggestion.status} />
            </div>
            <div className="flex items-center gap-x-2 text-sm text-text-secondary">
              <span>{reviewPositionLabel(position.index, position.total)}</span>
              {position.total > 1 ? (
                <>
                  <NavButton label="Previous suggestion" onClick={position.onPrevious} disabled={position.index === 0}>
                    <ChevronLeftIcon className="size-4" />
                  </NavButton>
                  <NavButton
                    label="Next suggestion"
                    onClick={position.onNext}
                    disabled={position.index === position.total - 1}
                  >
                    <ChevronRightIcon className="size-4" />
                  </NavButton>
                </>
              ) : null}
            </div>
          </div>

          <SuggestionKindNote suggestion={suggestion} orgName={org} />

          <SuggestionDetails
            suggestion={suggestion}
            draft={draft}
            catalogs={catalogs}
            catalogsPending={catalogsPending}
            orgName={orgName}
          />

          {note ? (
            <p className="rounded-lg border border-blurple-200 bg-blurple-50 px-4 py-2 text-sm text-text-primary">
              {note}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-4">
            {!stale ? (
              <ul className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-text-secondary">
                {[
                  approvalScopeLabel(suggestion, org),
                  "Tested side by side before it goes live",
                  "Needs clinician approval",
                ].map((point) => (
                  <li key={point} className="flex items-center gap-x-1.5">
                    <span className="size-1 rounded-full bg-blurple-400" aria-hidden="true" />
                    {point}
                  </li>
                ))}
              </ul>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-x-3">
              {suggestion.can_dismiss ? (
                <Button
                  text="Dismiss"
                  variant="secondary"
                  buttonStyle="ghost"
                  size="sm"
                  onClick={() => setConfirmingDismiss(true)}
                />
              ) : null}
              <CustomerSummaryButton suggestion={suggestion} />
              {stale ? (
                <Button
                  text={
                    beingIncorporated
                      ? "Incorporating…"
                      : suggestion.redraft_in_progress || redraftMutation.isPending
                        ? "Re-drafting…"
                        : "Re-draft"
                  }
                  variant="primary"
                  buttonStyle="filled"
                  size="sm"
                  onClick={() => void redraft()}
                  disabled={!suggestion.can_redraft || redraftMutation.isPending}
                />
              ) : (
                <>
                  <Button
                    text="Edit suggestion"
                    variant="secondary"
                    buttonStyle="border"
                    size="sm"
                    onClick={() => setEditing(true)}
                    disabled={nothingLeft}
                  />
                  {nothingLeft ? null : (
                    <Button
                      text="Review & test"
                      variant="primary"
                      buttonStyle="filled"
                      size="sm"
                      onClick={() => navigate(suggestionEvaluationsPath(suggestion.prompt_key, suggestion.id))}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={confirmingDismiss}
        setOpen={setConfirmingDismiss}
        title="Dismiss this suggestion?"
        size="md"
        primaryButton={{
          text: dismissMutation.isPending ? "Dismissing…" : "Dismiss",
          onClick: () => void dismiss(),
          disabled: dismissMutation.isPending,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setConfirmingDismiss(false),
          disabled: dismissMutation.isPending,
        }}
      >
        <Paragraph size="small" colorType="secondary">
          Rio won&apos;t suggest &ldquo;{suggestion.theme_title}&rdquo; for {suggestion.prompt_key} again, even if the
          theme comes back next week.
        </Paragraph>
      </Dialog>
      {editing ? <EditDraftDialog suggestion={suggestion} draft={draft} onClose={() => setEditing(false)} /> : null}
    </section>
  );
};

/** Rio's suggestions still to review for one org, one at a time.
 *  Renders nothing when there are none. Keyed by org at the call site, so
 *  switching org starts from the first. */
export const PromptSuggestions: FC<{ orgId: number }> = ({ orgId }) => {
  const { data, isError } = useListPromptSuggestions(
    { organization: orgId },
    {
      query: {
        // Until a re-draft writes back.
        refetchInterval: (query) => {
          const resp = query.state.data;
          return resp?.status === 200 && resp.data.suggestions.some((s) => s.redraft_in_progress)
            ? REDRAFT_POLL_MS
            : false;
        },
      },
    },
  );
  // A shared prompt with suggestions from more than one org is reviewed from
  // SharedPromptSuggestions instead. Shared ones wait until that's known, and
  // show here if it can't be.
  const { data: sharedResp, isError: sharedFailed } = useListSharedPromptChanges();
  const suggestions = useMemo(() => {
    if (data?.status !== 200) return [];
    const multiOrg =
      sharedResp?.status === 200
        ? new Set(multiOrgSharedGroups(sharedResp.data.groups).map((g) => g.prompt_key))
        : sharedFailed || sharedResp !== undefined
          ? new Set<string>()
          : null;
    return data.data.suggestions.filter(
      (s) => s.kind !== PromptSuggestionKindEnum.shared_change || (multiOrg !== null && !multiOrg.has(s.prompt_key)),
    );
  }, [data, sharedResp, sharedFailed]);
  const drafts = useMemo(() => suggestions.map(suggestionDraft), [suggestions]);
  const [index, setIndex] = useState(0);

  // Every suggestion here is the org's.
  const orgName = suggestions[0]?.organization_name ?? null;

  const { catalogs, catalogsPending } = useSuggestionCatalogs(drafts);

  // The Orval fetcher resolves on 4xx — check status before reading the body.
  const failed = isError || (data !== undefined && data.status !== 200);
  if (failed) {
    return (
      <Paragraph displayType="normal" size="small" className="text-text-secondary">
        Could not load prompt suggestions.
      </Paragraph>
    );
  }
  if (suggestions.length === 0) return null;

  // A dismissal shortens the list under the current position.
  const current = Math.min(index, suggestions.length - 1);
  return (
    <SuggestionCard
      key={suggestions[current].id}
      suggestion={suggestions[current]}
      draft={drafts[current]}
      catalogs={catalogs}
      catalogsPending={catalogsPending}
      orgName={orgName}
      position={{
        index: current,
        total: suggestions.length,
        onPrevious: () => setIndex(current - 1),
        onNext: () => setIndex(current + 1),
      }}
    />
  );
};
